import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Socket } from "socket.io-client";
import {
  apiRequest,
  addChatMember,
  approveChatJoinRequest,
  claimDeviceLink,
  claimGroupInvite,
  createDeviceLink as createDeviceLinkRequest,
  createGroupInvite,
  decideDeviceLinkApproval,
  deleteGroup,
  deleteMessageForMe,
  getAccountDevices,
  getChatJoinRequests,
  getChats,
  getDeviceLink,
  getDeviceLinkApprovals,
  getGroupBans,
  getMessages,
  getMessagePage,
  getPublicGroups,
  getUserProfile,
  getUserDevices,
  importDeviceKeyEnvelopes,
  leaveGroup,
  loadSession,
  logout,
  rejectChatJoinRequest,
  retractMessage,
  removeChatMember,
  requestPublicGroupJoin,
  revokeAccountDevice,
  setChatMemberRole,
  setMessagePinned,
  setMessageReaction,
  updateChatNotifications,
  updateGroupAvatar,
  updateGroupProfile,
  updateMessage,
  unblockGroupMember,
} from "./api";
import { useCallController } from "./calls";
import { ATTACHMENT_CHUNK_BYTES, createAttachmentV2Context, decryptAttachment, decryptAttachmentChunk, decryptRoomMessage, encryptAttachment, encryptAttachmentChunk, encryptRoomMessage, openRoomKey, sealRoomKey, createRoomKey } from "./crypto";
import { connectSocket } from "./socket";
import { indexMessages, removeIndexedMessage } from "./localMessages";
import {
  buildDeviceLinkUrl,
  createEncryptedDeviceLink,
  createRecoveryDocument,
  mergeRoomKeys,
  openEncryptedDeviceLink,
  openRecoveryDocument,
  readDeviceLink,
} from "./recovery";
import type { AccountDevice, Chat, ChatJoinRequest, ChatMember, DecryptedMessage, DeviceIdentity, DeviceKeyEnvelopeInput, DeviceLinkApproval, GroupBan, PlainMessage, PublicGroup, RoomKeyStore, Session, User, WireMessage } from "./types";
import { AuthScreen } from "./components/AuthScreen";
import { CallOverlay } from "./components/CallOverlay";
import { ChatView } from "./components/ChatView";
import { CreateChatModal, type CreateChatInput } from "./components/CreateChatModal";
import { DeviceManagerModal } from "./components/DeviceManagerModal";
import { GroupDirectoryModal } from "./components/GroupDirectoryModal";
import { GroupManagerModal } from "./components/GroupManagerModal";
import { notificationUnreadCount, Sidebar } from "./components/Sidebar";
import { UnlockScreen } from "./components/UnlockScreen";
import { disableWebPush, enableWebPush, setApplicationBadge } from "./webPush";
import { AdminModal } from "./components/AdminModal";
import { useGroupCallController } from "./groupCalls";
import { GroupCallOverlay } from "./components/GroupCallOverlay";
import { SecurityVerificationModal, type SecurityVerificationData } from "./components/SecurityVerificationModal";
import { computeSecurityCode, saveSecurityVerification, securityVerificationState } from "./security";
import { ProfileModal } from "./components/ProfileModal";
import { isCompletedKeyTransferRace, keyRequestPollDelay, retryKeyTransfer } from "./keyTransfer";
import { cleanConsumedChatRoute, consumeRequestedChatSelection } from "./chatNavigation";
import { isNativeMobileShell, postNativeMessage, setNativeCallAudioRoute, type NativeCallAudioRoute } from "./mobileBridge";
import {
  createSystemVaultSetup,
  saveIdentity,
  saveIdentityAutomatic,
  saveIdentityWithNativeSystem,
  saveIdentityWithSystem,
  setAppLockPreference,
  type VaultProtection,
} from "./vault";

async function sealKeyStoreForDevice(keys: RoomKeyStore, encryptionPublicKey: string): Promise<DeviceKeyEnvelopeInput[]> {
  const result: DeviceKeyEnvelopeInput[] = [];
  for (const [chatId, epochs] of keys) {
    for (const [epoch, key] of epochs) {
      result.push({ chatId, epoch, envelope: await sealRoomKey(key, encryptionPublicKey) });
    }
  }
  return result;
}

function roomKeyCount(keys: RoomKeyStore): number {
  let count = 0;
  for (const epochs of keys.values()) count += epochs.size;
  return count;
}

function availableRoomKeys(keys: RoomKeyStore, chats: readonly Chat[]): RoomKeyStore {
  const epochsByChat = new Map(chats.map((chat) => [chat.id, chat.currentEpoch]));
  const result: RoomKeyStore = new Map();
  for (const [chatId, epochs] of keys) {
    const currentEpoch = epochsByChat.get(chatId);
    if (!currentEpoch) continue;
    const available = new Map([...epochs].filter(([epoch]) => epoch <= currentEpoch));
    if (available.size > 0) result.set(chatId, available);
  }
  return result;
}

async function createGroupEpochBundle(userIds: readonly string[]): Promise<{
  envelopes: Array<{ deviceId: string; envelope: string }>;
  roomKey: Uint8Array;
}> {
  const uniqueUserIds = [...new Set(userIds)];
  const deviceGroups = await Promise.all(uniqueUserIds.map(getUserDevices));
  if (deviceGroups.some((devices) => devices.length === 0)) throw new Error("У одного из участников нет активного устройства");
  const devices = deviceGroups.flat();
  const roomKey = await createRoomKey();
  const envelopes = await Promise.all(devices.map(async (device) => ({
    deviceId: device.id,
    envelope: await sealRoomKey(roomKey, device.encryptionPublicKey),
  })));
  return { roomKey, envelopes };
}

function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [identity, setIdentity] = useState<DeviceIdentity | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DecryptedMessage[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [notificationMessageId, setNotificationMessageId] = useState<string | null>(
    new URLSearchParams(window.location.search).get("message"),
  );
  const [requestingKey, setRequestingKey] = useState(false);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [deviceManagerOpen, setDeviceManagerOpen] = useState(false);
  const [groupDirectoryOpen, setGroupDirectoryOpen] = useState(false);
  const [groupManagerOpen, setGroupManagerOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [localProtection, setLocalProtection] = useState<VaultProtection>("automatic");
  const [devices, setDevices] = useState<AccountDevice[]>([]);
  const [deviceApprovals, setDeviceApprovals] = useState<DeviceLinkApproval[]>([]);
  const [securityData, setSecurityData] = useState<SecurityVerificationData | null>(null);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [securityStatuses, setSecurityStatuses] = useState<Record<string, "unknown" | "verified" | "changed">>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dndEnabled, setDndEnabled] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [fileTransfer, setFileTransfer] = useState<{ attachmentId?: string; direction: "download" | "upload"; name: string; progress: number } | null>(null);
  const keysRef = useRef<RoomKeyStore>(new Map());
  const selectedRef = useRef<string | null>(null);
  const chatsRef = useRef<Chat[]>([]);
  const keySyncRef = useRef<Promise<void>>(Promise.resolve());
  const chatRefreshSequenceRef = useRef(0);
  const deviceRefreshSequenceRef = useRef(0);
  const claimedDeviceLinkRef = useRef<string | null>(null);
  const claimedGroupInviteRef = useRef<string | null>(null);
  const fileTransferAbortRef = useRef<AbortController | null>(null);
  const telemetryCallRef = useRef<string | null>(null);
  const requestedChatRef = useRef<string | null>(new URLSearchParams(window.location.search).get("chat"));

  useEffect(() => {
    void loadSession().then(setSession).catch((caught) => {
      setError(caught instanceof Error ? caught.message : "Сервис временно недоступен");
      setSession(null);
    });
  }, []);

  useEffect(() => {
    setDndEnabled(Boolean(session?.user.dndUntil && new Date(session.user.dndUntil).getTime() > Date.now()));
    if (session && "Notification" in window && Notification.permission === "granted") {
      void enableWebPush().then(setPushEnabled).catch(() => setPushEnabled(false));
    }
  }, [session]);

  useEffect(() => {
    void setApplicationBadge(chats.reduce((total, chat) => total + notificationUnreadCount(chat), 0)).catch(() => undefined);
  }, [chats]);

  const unlockChatKeys = useCallback(async (nextChats: Chat[], activeIdentity: DeviceIdentity) => {
    const nextStore: RoomKeyStore = new Map(keysRef.current);
    for (const chat of nextChats) {
      const epochs = new Map(nextStore.get(chat.id));
      for (const item of chat.keyEnvelopes) {
        if (epochs.has(item.epoch)) continue;
        try {
          epochs.set(item.epoch, await openRoomKey(item.envelope, activeIdentity));
        } catch {
          // A stale or malformed envelope is skipped; the UI can request the current key.
        }
      }
      nextStore.set(chat.id, epochs);
    }
    keysRef.current = nextStore;
  }, []);

  const refreshChats = useCallback(async () => {
    if (!identity) return;
    const sequence = ++chatRefreshSequenceRef.current;
    const next = await getChats();
    await unlockChatKeys(next, identity);
    // A slower, older response must never restore an obsolete unread counter.
    if (sequence !== chatRefreshSequenceRef.current) return;
    setChats(next);
    chatsRef.current = next;
    const requestedChatId = requestedChatRef.current;
    requestedChatRef.current = null;
    setSelectedId((current) => consumeRequestedChatSelection(next, current, requestedChatId).selectedId);
    if (requestedChatId) {
      window.history.replaceState(null, "", cleanConsumedChatRoute(window.location.href));
    }
  }, [identity, unlockChatKeys]);

  const refreshDevices = useCallback(async () => {
    const sequence = ++deviceRefreshSequenceRef.current;
    const [nextDevices, approvals] = await Promise.all([getAccountDevices(), getDeviceLinkApprovals()]);
    if (sequence !== deviceRefreshSequenceRef.current) return;
    setDevices(nextDevices.filter((device) => !device.revokedAt));
    setDeviceApprovals(approvals);
  }, []);

  useEffect(() => {
    const navigateInsideSession = (event: Event) => {
      const detail = (event as CustomEvent<{ chatId?: string | null; messageId?: string | null }>).detail;
      const chatId = detail?.chatId;
      if (!chatId) return;
      setNotificationMessageId(detail.messageId ?? null);
      const existing = chatsRef.current.some((chat) => chat.id === chatId);
      if (existing) {
        selectedRef.current = chatId;
        setSelectedId(chatId);
      } else {
        requestedChatRef.current = chatId;
        void refreshChats().catch((caught) => setError(caught instanceof Error ? caught.message : "Не удалось открыть чат из уведомления"));
      }
      window.history.replaceState(null, "", cleanConsumedChatRoute(window.location.href));
    };
    window.addEventListener("jtuc:native-navigation", navigateInsideSession as EventListener);
    return () => window.removeEventListener("jtuc:native-navigation", navigateInsideSession as EventListener);
  }, [refreshChats]);

  useEffect(() => {
    if (!session || !isNativeMobileShell()) return;
    const registerNativeToken = () => void enableWebPush().then(setPushEnabled).catch(() => setPushEnabled(false));
    // The native shell may obtain its FCM token before the WebView finishes
    // login/unlock. Register immediately after a server session appears, and
    // also listen for future token rotations.
    registerNativeToken();
    window.addEventListener("jtuc:native-push-token", registerNativeToken);
    return () => {
      window.removeEventListener("jtuc:native-push-token", registerNativeToken);
    };
  }, [session]);

  useEffect(() => {
    if (!session || !identity) return;
    const link = readDeviceLink(window.location.hash);
    if (!link || claimedDeviceLinkRef.current === link.linkId) return;
    claimedDeviceLinkRef.current = link.linkId;
    void (async () => {
      const encrypted = await getDeviceLink(link.linkId);
      const decrypted = await openEncryptedDeviceLink(
        encrypted.ciphertext,
        encrypted.nonce,
        link.secret,
        link.linkId,
        session.user.username,
      );
      const incoming = availableRoomKeys(decrypted, await getChats());
      const envelopes = await sealKeyStoreForDevice(incoming, identity.encryptionPublicKey);
      await claimDeviceLink(link.linkId, envelopes);
      const imported = mergeRoomKeys(keysRef.current, incoming);
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
      await Promise.all([refreshChats(), refreshDevices()]);
      setError(`Новое устройство подключено. Получено ключей: ${imported}.`);
    })().catch((caught) => {
      claimedDeviceLinkRef.current = null;
      setError(caught instanceof Error ? caught.message : "Не удалось подключить устройство");
    });
  }, [identity, refreshChats, refreshDevices, session]);

  useEffect(() => {
    if (!session || !identity) return;
    const token = new URLSearchParams(window.location.search).get("invite");
    if (!token || claimedGroupInviteRef.current === token) return;
    claimedGroupInviteRef.current = token;
    void claimGroupInvite(token).then(async (result) => {
      const url = new URL(window.location.href);
      url.searchParams.delete("invite");
      window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
      await refreshChats();
      if (result.alreadyMember) setSelectedId(result.chatId);
      setError(result.alreadyMember ? "Группа открыта." : "Запрос на вступление отправлен администраторам группы.");
    }).catch((caught) => {
      claimedGroupInviteRef.current = null;
      setError(caught instanceof Error ? caught.message : "Не удалось применить приглашение");
    });
  }, [identity, refreshChats, session]);

  const decryptWire = useCallback(async (wire: WireMessage): Promise<DecryptedMessage> => {
    const key = keysRef.current.get(wire.chatId)?.get(wire.epoch);
    if (!key) return { wire, plain: null, authenticity: "verified" };
    try {
      return { wire, plain: await decryptRoomMessage(wire, key), authenticity: "verified" };
    } catch (caught) {
      return { wire, plain: null, authenticity: caught instanceof Error && caught.message === "INVALID_SIGNATURE" ? "invalid" : "verified" };
    }
  }, []);

  const fulfillPendingKeys = useCallback(async (chatId?: string) => {
    const response = await apiRequest<{
      requests: Array<{ chatId: string; device: { encryptionPublicKey: string; id: string }; epoch: number; id: string }>;
    }>(`/api/key-requests/pending${chatId ? `?chatId=${encodeURIComponent(chatId)}` : ""}`);
    const failures: unknown[] = [];
    for (let offset = 0; offset < response.requests.length; offset += 4) {
      const batch = response.requests.slice(offset, offset + 4);
      const results = await Promise.allSettled(batch.map(async (request) => {
        const key = keysRef.current.get(request.chatId)?.get(request.epoch);
        if (!key || request.device.id === identity?.id) return;
        const envelope = await sealRoomKey(key, request.device.encryptionPublicKey);
        try {
          await retryKeyTransfer(() => apiRequest<void>(
            `/api/chats/${request.chatId}/key-requests/${request.id}/fulfill`,
            { method: "POST", body: { envelope } },
          ));
        } catch (caught) {
          // Another authorized device may have won the race while this device
          // was encrypting. The idempotent server also accepts this case.
          if (!isCompletedKeyTransferRace(caught)) throw caught;
        }
      }));
      for (const result of results) if (result.status === "rejected") failures.push(result.reason);
    }
    if (failures.length > 0) throw failures[0];
  }, [identity?.id]);

  const synchronizePendingKeys = useCallback(async (chatId?: string) => {
    // Serialize runs so reconnect, WebSocket and foreground events cannot
    // encrypt and submit the same queue concurrently on one device.
    keySyncRef.current = keySyncRef.current
      .catch(() => undefined)
      .then(() => fulfillPendingKeys(chatId));
    await keySyncRef.current;
  }, [fulfillPendingKeys]);

  useEffect(() => {
    if (!session || !identity) return;
    const synchronize = (refresh: boolean) => {
      if (document.visibilityState === "hidden" || !navigator.onLine) return;
      const operation = refresh ? refreshChats() : Promise.resolve();
      void operation.then(() => synchronizePendingKeys()).catch(() => undefined);
    };
    const onVisibility = () => { if (document.visibilityState === "visible") synchronize(true); };
    const onNativePush = () => synchronize(true);
    const onFocus = () => synchronize(true);
    const onOnline = () => synchronize(true);
    const interval = window.setInterval(() => synchronize(false), 8_000);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("jtuc:native-push", onNativePush);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("jtuc:native-push", onNativePush);
    };
  }, [identity, refreshChats, session, synchronizePendingKeys]);

  const markChatRead = useCallback(async (chatId: string) => {
    if (document.visibilityState !== "visible" || selectedRef.current !== chatId) return;
    await apiRequest<void>(`/api/chats/${chatId}/read`, {
      method: "POST",
    });
    setChats((current) => {
      const next = current.map((chat) => chat.id === chatId ? { ...chat, mentionCount: 0, unreadCount: 0 } : chat);
      chatsRef.current = next;
      return next;
    });
    // This request invalidates any list refresh that started before the read marker was stored.
    await refreshChats();
  }, [refreshChats]);

  const reloadChatMessages = useCallback(async (chatId: string): Promise<DecryptedMessage[]> => {
    const wireMessages = await getMessages(chatId);
    const decrypted = await Promise.all(wireMessages.map(decryptWire));
    if (selectedRef.current === chatId) {
      setMessages(decrypted);
      void indexMessages(decrypted).catch(() => undefined);
      await markChatRead(chatId).catch(() => undefined);
    }
    return decrypted;
  }, [decryptWire, markChatRead]);

  useEffect(() => {
    if (!session || !identity) return;
    void refreshChats().catch((caught) => setError(caught instanceof Error ? caught.message : "Не удалось загрузить чаты"));
    const connection = connectSocket();
    setSocket(connection);
    const onConnect = () => {
      setConnected(true);
      connection.emit("chat:sync");
      void refreshChats().then(() => synchronizePendingKeys()).catch(() => undefined);
    };
    const onDisconnect = () => setConnected(false);
    const onConnectError = () => void apiRequest<void>("/api/telemetry", { method: "POST", body: { metric: "websocket_error" } }).catch(() => undefined);
    const onChatChanged = () => {
      connection.emit("chat:sync");
      void refreshChats();
    };
    const onProfileChanged = (value: { userId?: string }) => {
      void refreshChats();
      if (value.userId === session.user.id) {
        void getUserProfile(session.user.id).then((profile) => setSession((current) => current ? { ...current, user: { ...current.user, ...profile.user } } : current)).catch(() => undefined);
      }
    };
    const onChatDeleted = (value: { chatId: string }) => {
      keysRef.current.delete(value.chatId);
      if (selectedRef.current === value.chatId) {
        selectedRef.current = null;
        setSelectedId(null);
        setGroupManagerOpen(false);
        setMessages([]);
      }
      connection.emit("chat:sync");
      void refreshChats();
    };
    const onKeyReady = (value: { chatId: string; epoch: number }) => {
      // A newly fulfilled request may replace a decryptable but incorrect envelope.
      // Discard the cached epoch so refreshChats opens the envelope received from the server.
      keysRef.current.get(value.chatId)?.delete(value.epoch);
      void refreshChats().then(async () => {
        if (selectedRef.current !== value.chatId) return;
        await reloadChatMessages(value.chatId);
      }).catch(() => undefined);
    };
    const onKeyRequest = (value: { chatId: string }) => void synchronizePendingKeys(value.chatId).catch(() => undefined);
    const onChatRead = (value: { chatId: string }) => {
      setChats((current) => {
        const next = current.map((chat) => chat.id === value.chatId ? { ...chat, mentionCount: 0, unreadCount: 0 } : chat);
        chatsRef.current = next;
        return next;
      });
    };
    const onDeviceLinkClaimed = () => void refreshDevices().catch(() => undefined);
    const onDeviceApprovalRequested = () => {
      setDeviceManagerOpen(true);
      void refreshDevices().catch(() => undefined);
    };
    const onDeviceApprovalDecided = () => void refreshDevices().catch(() => undefined);
    const onNewMessage = (wire: WireMessage) => {
      void decryptWire(wire).then(async (decrypted) => {
        if (wire.chatId === selectedRef.current) {
          setMessages((current) => {
            const next = current.some((item) => item.wire.id === wire.id) ? current : [...current, decrypted];
            void indexMessages([decrypted]).catch(() => undefined);
            return next;
          });
          if (wire.senderId !== session.user.id) await markChatRead(wire.chatId).catch(() => undefined);
        }
        if (wire.senderId !== session.user.id) connection.emit("message:receipt", { messageId: wire.id, state: "delivered" });
        if (wire.chatId !== selectedRef.current || wire.senderId === session.user.id) await refreshChats();
      });
    };
    const onMessageUpdated = (wire: WireMessage) => {
      void decryptWire(wire).then((decrypted) => {
        setMessages((current) => current.map((item) => item.wire.id === wire.id ? {
          ...decrypted,
          wire: { ...item.wire, ...decrypted.wire },
        } : item));
        void indexMessages([decrypted]).catch(() => undefined);
      });
    };
    const onMessageRetracted = (value: { messageId: string }) => {
      setMessages((current) => current.map((item) => item.wire.id === value.messageId ? {
        ...item,
        plain: null,
        wire: { ...item.wire, retractedAt: new Date().toISOString() },
      } : item));
      void removeIndexedMessage(value.messageId).catch(() => undefined);
    };
    const onMessageReaction = (value: { active: boolean; emoji: string; messageId: string; userId: string }) => {
      setMessages((current) => current.map((item) => {
        if (item.wire.id !== value.messageId) return item;
        const reactions = (item.wire.reactions ?? []).filter((reaction) => !(reaction.userId === value.userId && reaction.emoji === value.emoji));
        if (value.active) reactions.push({ userId: value.userId, emoji: value.emoji });
        return { ...item, wire: { ...item.wire, reactions } };
      }));
    };
    const onMessagePinned = (value: { messageId: string; pinned: boolean }) => {
      setMessages((current) => current.map((item) => item.wire.id === value.messageId ? { ...item, wire: { ...item.wire, pinned: value.pinned } } : item));
    };
    const onMessageReceipt = (value: { messageId: string; state: "delivered" | "read"; userId: string }) => {
      setMessages((current) => current.map((item) => {
        if (item.wire.id !== value.messageId) return item;
        const receipts = (item.wire.receipts ?? []).filter((receipt) => receipt.userId !== value.userId);
        receipts.push({ userId: value.userId, state: value.state, updatedAt: new Date().toISOString() });
        return { ...item, wire: { ...item.wire, receipts } };
      }));
    };
    const onReadUpTo = (value: { chatId: string; messageId: string; userId: string }) => {
      if (value.chatId !== selectedRef.current) return;
      setMessages((current) => {
        const boundary = current.findIndex((item) => item.wire.id === value.messageId);
        if (boundary < 0) return current;
        return current.map((item, index) => {
          if (index > boundary || item.wire.senderId !== session.user.id) return item;
          const receipts = (item.wire.receipts ?? []).filter((receipt) => receipt.userId !== value.userId);
          receipts.push({ userId: value.userId, state: "read", updatedAt: new Date().toISOString() });
          return { ...item, wire: { ...item.wire, receipts } };
        });
      });
    };
    connection.on("connect", onConnect);
    connection.on("disconnect", onDisconnect);
    connection.on("connect_error", onConnectError);
    connection.on("session:revoked", () => void doLogout());
    connection.on("chat:changed", onChatChanged);
    connection.on("profile:changed", onProfileChanged);
    connection.on("chat:deleted", onChatDeleted);
    connection.on("chat:rekeyed", onChatChanged);
    connection.on("chat:key-ready", onKeyReady);
    connection.on("chat:key-request", onKeyRequest);
    connection.on("chat:read", onChatRead);
    connection.on("chat:join-request", onChatChanged);
    connection.on("chat:join-request-decided", onChatChanged);
    connection.on("device-link:claimed", onDeviceLinkClaimed);
    connection.on("device-link:approval-requested", onDeviceApprovalRequested);
    connection.on("device-link:approval-decided", onDeviceApprovalDecided);
    connection.on("device-link:session-created", onDeviceLinkClaimed);
    connection.on("message:new", onNewMessage);
    connection.on("message:updated", onMessageUpdated);
    connection.on("message:retracted", onMessageRetracted);
    connection.on("message:reaction", onMessageReaction);
    connection.on("message:pinned", onMessagePinned);
    connection.on("message:receipt", onMessageReceipt);
    connection.on("message:read-up-to", onReadUpTo);
    return () => {
      connection.off();
      connection.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [decryptWire, identity, markChatRead, refreshChats, refreshDevices, reloadChatMessages, session, synchronizePendingKeys]);

  useEffect(() => {
    selectedRef.current = selectedId;
    if (!selectedId) {
      setMessages([]);
      setNextBefore(null);
      return;
    }
    void markChatRead(selectedId).catch(() => undefined);
    setLoadingMessages(true);
    void getMessagePage(selectedId, undefined, 50)
      .then(async (page) => ({ ...page, decrypted: await Promise.all(page.messages.map(decryptWire)) }))
      .then(({ decrypted, nextBefore: cursor }) => {
        setMessages(decrypted);
        setNextBefore(cursor);
        void indexMessages(decrypted).catch(() => undefined);
      })
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Не удалось загрузить историю"))
      .finally(() => setLoadingMessages(false));
  }, [decryptWire, markChatRead, selectedId]);

  const loadOlderMessages = async (): Promise<void> => {
    if (!selectedId || !nextBefore || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const page = await getMessagePage(selectedId, nextBefore, 50);
      const decrypted = await Promise.all(page.messages.map(decryptWire));
      setMessages((current) => {
        const existing = new Set(current.map((message) => message.wire.id));
        return [...decrypted.filter((message) => !existing.has(message.wire.id)), ...current];
      });
      setNextBefore(page.nextBefore);
      void indexMessages(decrypted).catch(() => undefined);
    } finally {
      setLoadingOlder(false);
    }
  };

  useEffect(() => {
    if (!notificationMessageId || !selectedId || loadingMessages || loadingOlder || messages.some((message) => message.wire.id === notificationMessageId) || !nextBefore) return;
    void loadOlderMessages().catch((caught) => setError(caught instanceof Error ? caught.message : "Не удалось открыть сообщение из уведомления"));
  }, [loadingMessages, loadingOlder, messages, nextBefore, notificationMessageId, selectedId]);

  useEffect(() => {
    if (!notificationMessageId || !messages.some((message) => message.wire.id === notificationMessageId)) return;
    const timer = window.setTimeout(() => {
      document.getElementById(`message-${notificationMessageId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setNotificationMessageId(null);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [messages, notificationMessageId]);

  useEffect(() => {
    const onVisibility = () => {
      const chatId = selectedRef.current;
      if (chatId) void markChatRead(chatId).catch(() => undefined);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [markChatRead]);

  const selectedChat = chats.find((chat) => chat.id === selectedId) ?? null;
  const currentKey = selectedChat ? keysRef.current.get(selectedChat.id)?.get(selectedChat.currentEpoch) : undefined;

  const loadSecurityData = useCallback(async (chat: Chat): Promise<SecurityVerificationData> => {
    if (!session || chat.kind !== "direct") throw new Error("Проверка ключей доступна только для личного чата");
    const peer = chat.members.find((member) => member.id !== session.user.id);
    if (!peer) throw new Error("Собеседник не найден");
    const [ownDevices, peerDevices] = await Promise.all([getUserDevices(session.user.id), getUserDevices(peer.id)]);
    const code = await computeSecurityCode(ownDevices, peerDevices);
    return { code, ownDevices, peerDevices, state: securityVerificationState(chat.id, code) };
  }, [session]);

  useEffect(() => {
    if (!selectedChat || selectedChat.kind !== "direct") return;
    let active = true;
    void loadSecurityData(selectedChat)
      .then((data) => { if (active) setSecurityStatuses((current) => ({ ...current, [selectedChat.id]: data.state })); })
      .catch(() => { if (active) setSecurityStatuses((current) => ({ ...current, [selectedChat.id]: "unknown" })); });
    return () => { active = false; };
  }, [loadSecurityData, selectedChat]);

  useEffect(() => {
    setSecurityOpen(false);
    setSecurityData(null);
  }, [selectedId]);

  const openSecurityVerification = async (): Promise<void> => {
    if (!selectedChat || selectedChat.kind !== "direct") return;
    try {
      const data = await loadSecurityData(selectedChat);
      setSecurityData(data);
      setSecurityStatuses((current) => ({ ...current, [selectedChat.id]: data.state }));
      setSecurityOpen(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось загрузить ключи собеседника");
    }
  };

  const refreshSecurityVerification = async (): Promise<SecurityVerificationData> => {
    if (!selectedChat || selectedChat.kind !== "direct") throw new Error("Личный чат не выбран");
    const data = await loadSecurityData(selectedChat);
    setSecurityData(data);
    setSecurityStatuses((current) => ({ ...current, [selectedChat.id]: data.state }));
    return data;
  };

  const verifySecurity = (): void => {
    if (!selectedChat || !securityData) return;
    saveSecurityVerification(selectedChat.id, securityData.code);
    setSecurityData((current) => current ? { ...current, state: "verified" } : current);
    setSecurityStatuses((current) => ({ ...current, [selectedChat.id]: "verified" }));
  };

  const sendWire = async (wire: WireMessage): Promise<void> => {
    if (!socket?.connected) throw new Error("Нет соединения с сервером");
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await new Promise<void>((resolve, reject) => {
          socket.timeout(10_000).emit("message:send", wire, (timeout: Error | null, response: { error?: { message: string }; ok?: true }) => {
            if (timeout) return reject(new Error("DELIVERY_ACK_TIMEOUT"));
            if (response.error) return reject(new Error(response.error.message));
            resolve();
          });
        });
        return;
      } catch (caught) {
        if (!(caught instanceof Error) || caught.message !== "DELIVERY_ACK_TIMEOUT" || attempt === 2) {
          if (caught instanceof Error && caught.message === "DELIVERY_ACK_TIMEOUT") {
            throw new Error("Сервер не подтвердил доставку после трёх попыток. Черновик сохранён в поле ввода.");
          }
          throw caught;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
  };

  const sendText = async (text: string, replyTo: string | null = null) => {
    if (!selectedChat || !currentKey || !identity || !session) return;
    setError(null);
    try {
      await sendWire(await encryptRoomMessage({ type: "text", text }, currentKey, identity, {
        chatId: selectedChat.id,
        epoch: selectedChat.currentEpoch,
        kind: "text",
        mentionUserIds: selectedChat.members.filter((member) => text.toLocaleLowerCase("ru-RU").includes(`@${member.username.toLocaleLowerCase("ru-RU")}`)).map((member) => member.id),
        replyTo,
        senderId: session.user.id,
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось отправить сообщение");
      throw caught;
    }
  };

  const editMessage = async (message: DecryptedMessage, text: string): Promise<void> => {
    if (!selectedChat || !currentKey || !identity || !session) return;
    const wire = await encryptRoomMessage({ type: "text", text }, currentKey, identity, {
      chatId: selectedChat.id,
      clientCreatedAt: message.wire.clientCreatedAt,
      epoch: selectedChat.currentEpoch,
      forwardedFrom: message.wire.forwardedFrom ?? null,
      id: message.wire.id,
      kind: "text",
      mentionUserIds: selectedChat.members.filter((member) => text.toLocaleLowerCase("ru-RU").includes(`@${member.username.toLocaleLowerCase("ru-RU")}`)).map((member) => member.id),
      replyTo: message.wire.replyTo,
      senderId: session.user.id,
    });
    const updated = await updateMessage(selectedChat.id, message.wire.id, wire);
    const decrypted = await decryptWire({ ...message.wire, ...updated });
    setMessages((current) => current.map((item) => item.wire.id === message.wire.id ? decrypted : item));
    await indexMessages([decrypted]);
  };

  const deleteMessage = async (message: DecryptedMessage, everyone: boolean): Promise<void> => {
    if (!selectedChat) return;
    if (everyone) {
      if (!window.confirm("Удалить это сообщение у всех участников?")) return;
      await retractMessage(selectedChat.id, message.wire.id);
      setMessages((current) => current.map((item) => item.wire.id === message.wire.id ? { ...item, plain: null, wire: { ...item.wire, retractedAt: new Date().toISOString() } } : item));
    } else {
      await deleteMessageForMe(selectedChat.id, message.wire.id);
      setMessages((current) => current.filter((item) => item.wire.id !== message.wire.id));
    }
    await removeIndexedMessage(message.wire.id);
  };

  const reactMessage = async (message: DecryptedMessage, emoji: string, active: boolean): Promise<void> => {
    await setMessageReaction(message.wire.chatId, message.wire.id, emoji, active);
  };

  const pinMessage = async (message: DecryptedMessage, pinned: boolean): Promise<void> => {
    await setMessagePinned(message.wire.chatId, message.wire.id, pinned);
  };

  const reportMessage = async (message: DecryptedMessage): Promise<void> => {
    const content = message.plain ? (message.plain.type === "text" || message.plain.type === "system" ? message.plain.text : message.plain.type === "file" ? message.plain.name : "Голосовое сообщение") : "";
    if (!content) throw new Error("Сообщение нельзя раскрыть для жалобы");
    const category = window.prompt("Категория: spam, harassment, illegal или other", "spam");
    if (!category || !["spam", "harassment", "illegal", "other"].includes(category)) return;
    const comment = window.prompt("Необязательный комментарий модератору", "") ?? "";
    if (!window.confirm("Отправить жалобу? Вы явно разрешаете раскрыть модератору только выбранное расшифрованное сообщение. Остальная переписка не передаётся.")) return;
    await apiRequest("/api/reports", { method: "POST", body: { category, chatId: message.wire.chatId, comment, consent: true, disclosedContent: content, messageId: message.wire.id } });
    setError("Жалоба отправлена. Раскрыто только выбранное сообщение.");
  };

  const forwardMessage = async (message: DecryptedMessage): Promise<void> => {
    if (!identity || !session || !message.plain) throw new Error("Сообщение недоступно для пересылки");
    const targets = chats.filter((chat) => chat.id !== message.wire.chatId);
    if (targets.length === 0) throw new Error("Нет другого чата для пересылки");
    const choice = window.prompt(`Введите номер чата:\n${targets.map((chat, index) => `${index + 1}. ${chat.name ?? chat.members.find((member) => member.id !== session.user.id)?.displayName ?? "Чат"}`).join("\n")}`);
    const target = targets[Number(choice) - 1];
    if (!target) return;
    const key = keysRef.current.get(target.id)?.get(target.currentEpoch);
    if (!key) throw new Error("На этом устройстве нет ключа выбранного чата");
    if (message.plain.type === "file" || message.plain.type === "voice") {
      const sourceChat = chats.find((chat) => chat.id === message.wire.chatId);
      const allTargetsAlreadyAuthorized = target.members.every((member) => sourceChat?.members.some((sourceMember) => sourceMember.id === member.id));
      if (allTargetsAlreadyAuthorized) {
        await sendWire(await encryptRoomMessage(message.plain, key, identity, { chatId: target.id, epoch: target.currentEpoch, forwardedFrom: message.wire.id, kind: "file", senderId: session.user.id }));
      } else {
        const url = await resolveMedia(message.plain); const blob = await fetch(url).then((response) => response.blob()); URL.revokeObjectURL(url);
        const name = message.plain.type === "file" ? message.plain.name : `voice-${Date.now()}.${message.plain.mime.includes("mp4") ? "m4a" : "webm"}`;
        await sendFileToChat(new File([blob], name, { type: message.plain.mime }), target, key, message.wire.id);
      }
      return;
    }
    await sendWire(await encryptRoomMessage(message.plain, key, identity, {
      chatId: target.id,
      epoch: target.currentEpoch,
      forwardedFrom: message.wire.id,
      kind: message.plain.type === "system" ? "system" : "text",
      senderId: session.user.id,
    }));
  };

  const sendFileToChat = async (file: File, targetChat: Chat | null = selectedChat, targetKey: Uint8Array | undefined = currentKey, forwardedFrom: string | null = null) => {
    if (!targetChat || !targetKey || !identity || !session) return;
    setError(null);
    if (file.size === 0) throw new Error("Нельзя отправить пустой файл");
    if (file.size > 500 * 1024 * 1024) throw new Error("Максимальный размер исходного файла — 500 МБ");
    const abort = new AbortController();
    fileTransferAbortRef.current = abort;
    setFileTransfer({ direction: "upload", name: file.name, progress: 0 });
    try {
      const chunkCount = Math.ceil(file.size / ATTACHMENT_CHUNK_BYTES);
      const resumeKey = `jtuc-live:upload:${targetChat.id}:${file.name}:${file.size}:${file.lastModified}`;
      const saved = JSON.parse(localStorage.getItem(resumeKey) ?? "null") as { attachmentId: string; key: string; nonce: string } | null;
      let context = saved ? { key: saved.key, nonce: saved.nonce } : await createAttachmentV2Context();
      let attachmentId = saved?.attachmentId;
      if (!attachmentId) {
        const prepared = await apiRequest<{ attachmentId: string }>("/api/attachments/uploads", {
          method: "POST", signal: abort.signal,
          body: { chatId: targetChat.id, chunkCount, encryptedSize: file.size + chunkCount * 16 },
        });
        attachmentId = prepared.attachmentId;
        setFileTransfer({ attachmentId, direction: "upload", name: file.name, progress: 0 });
        localStorage.setItem(resumeKey, JSON.stringify({ attachmentId, ...context }));
      }
      setFileTransfer({ attachmentId, direction: "upload", name: file.name, progress: 0 });
      const status = await apiRequest<{ uploaded: number[] }>(`/api/attachments/${attachmentId}/upload-status`, { signal: abort.signal });
      const uploaded = new Set(status.uploaded);
      for (let index = 0; index < chunkCount; index += 1) {
        if (abort.signal.aborted) throw new DOMException("Загрузка отменена", "AbortError");
        if (!uploaded.has(index)) {
          const encrypted = await encryptAttachmentChunk(await file.slice(index * ATTACHMENT_CHUNK_BYTES, Math.min(file.size, (index + 1) * ATTACHMENT_CHUNK_BYTES)).arrayBuffer(), context.key, context.nonce, index, chunkCount);
          const form = new FormData(); form.append("file", encrypted.ciphertext, `chunk-${index}.bin`);
          let lastError: unknown;
          for (let attempt = 0; attempt < 3; attempt += 1) {
            try { await apiRequest<void>(`/api/attachments/${attachmentId}/chunks/${index}?sha256=${encrypted.sha256}&size=${encrypted.ciphertext.size}`, { method: "POST", body: form, signal: abort.signal }); lastError = null; break; }
            catch (caught) { lastError = caught; if (attempt < 2 && !abort.signal.aborted) await new Promise((resolve) => window.setTimeout(resolve, 600 * (attempt + 1))); }
          }
          if (lastError) throw lastError;
        }
        setFileTransfer({ attachmentId, direction: "upload", name: file.name, progress: Math.round(((index + 1) / chunkCount) * 100) });
      }
      await apiRequest<void>(`/api/attachments/${attachmentId}/complete`, { method: "POST", signal: abort.signal });
      localStorage.removeItem(resumeKey);
      const plain: PlainMessage = {
        type: "file",
        attachmentId,
        name: file.name.slice(0, 240),
        mime: file.type || "application/octet-stream",
        size: file.size,
        key: context.key,
        nonce: context.nonce,
        protocolVersion: 2,
        chunkCount,
      };
      await sendWire(await encryptRoomMessage(plain, targetKey, identity, {
        chatId: targetChat.id,
        epoch: targetChat.currentEpoch,
        forwardedFrom,
        kind: "file",
        senderId: session.user.id,
      }));
    } catch (caught) {
      void apiRequest<void>("/api/telemetry", { method: "POST", body: { metric: "file_failed" } }).catch(() => undefined);
      setError(caught instanceof Error ? caught.message : "Не удалось отправить файл");
      throw caught;
    } finally {
      fileTransferAbortRef.current = null;
      setFileTransfer(null);
    }
  };

  const sendFile = async (file: File): Promise<void> => sendFileToChat(file);

  const sendVoice = async (blob: Blob, durationMs: number): Promise<void> => {
    if (!selectedChat || !currentKey || !identity || !session) return;
    if (blob.size > 20 * 1024 * 1024) throw new Error("Голосовое сообщение слишком большое");
    const file = new File([blob], `voice-${Date.now()}.${blob.type.includes("mp4") ? "m4a" : "webm"}`, { type: blob.type });
    const encrypted = await encryptAttachment(file);
    const prepared = await apiRequest<{ attachmentId: string; uploadUrl: string }>("/api/attachments", {
      method: "POST",
      body: { chatId: selectedChat.id, encryptedSize: encrypted.ciphertext.size, ciphertextSha256: encrypted.sha256 },
    });
    const form = new FormData();
    form.append("file", encrypted.ciphertext, "ciphertext.bin");
    await apiRequest<void>(prepared.uploadUrl, { method: "POST", body: form });
    const plain: PlainMessage = {
      type: "voice",
      attachmentId: prepared.attachmentId,
      durationMs,
      mime: blob.type || "audio/webm",
      size: blob.size,
      key: encrypted.key,
      nonce: encrypted.nonce,
      waveform: [],
    };
    await sendWire(await encryptRoomMessage(plain, currentKey, identity, {
      chatId: selectedChat.id,
      epoch: selectedChat.currentEpoch,
      kind: "file",
      senderId: session.user.id,
    }));
  };

  const resolveMedia = async (plain: Extract<PlainMessage, { type: "file" | "voice" }>): Promise<string> => {
    if (plain.type === "file" && plain.protocolVersion === 2 && plain.chunkCount) {
      const abort = new AbortController(); fileTransferAbortRef.current = abort;
      setFileTransfer({ attachmentId: plain.attachmentId, direction: "download", name: plain.name, progress: 0 });
      const chunks: Uint8Array[] = [];
      try {
        for (let index = 0; index < plain.chunkCount; index += 1) {
          let response: Response | null = null;
          for (let attempt = 0; attempt < 3; attempt += 1) {
            response = await fetch(`/api/attachments/${plain.attachmentId}/chunks/${index}`, { credentials: "include", cache: "no-store", signal: abort.signal });
            if (response.ok) break;
            if (attempt < 2) await new Promise((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));
          }
          if (!response?.ok) throw new Error(`Не удалось скачать блок ${index + 1}`);
          chunks.push(await decryptAttachmentChunk(await response.arrayBuffer(), plain.key, plain.nonce, index, plain.chunkCount));
          setFileTransfer({ attachmentId: plain.attachmentId, direction: "download", name: plain.name, progress: Math.round(((index + 1) / plain.chunkCount) * 100) });
        }
        return URL.createObjectURL(new Blob(chunks.map((chunk) => Uint8Array.from(chunk).buffer), { type: plain.mime }));
      } finally {
        fileTransferAbortRef.current = null; setFileTransfer(null);
      }
    }
    const response = await fetch(`/api/attachments/${plain.attachmentId}/content`, { credentials: "include", cache: "no-store" });
    if (!response.ok) throw new Error("Не удалось скачать ciphertext файла");
    const decrypted = await decryptAttachment(await response.arrayBuffer(), plain.key, plain.nonce);
    return URL.createObjectURL(new Blob([Uint8Array.from(decrypted).buffer], { type: plain.mime }));
  };

  const downloadFile = async (plain: Extract<PlainMessage, { type: "file" | "voice" }>) => {
    const url = await resolveMedia(plain);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = plain.type === "file" ? plain.name : `voice-${Date.now()}.${plain.mime.includes("mp4") ? "m4a" : "webm"}`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const cancelFileTransfer = (): void => {
    const transfer = fileTransfer;
    fileTransferAbortRef.current?.abort();
    if (transfer?.direction === "upload" && transfer.attachmentId) void apiRequest<void>(`/api/attachments/${transfer.attachmentId}`, { method: "DELETE" }).catch(() => undefined);
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (transfer?.direction === "upload" && key?.startsWith("jtuc-live:upload:") && transfer.attachmentId && localStorage.getItem(key)?.includes(transfer.attachmentId)) localStorage.removeItem(key);
    }
    setFileTransfer(null);
  };

  const createChat = async (input: CreateChatInput) => {
    if (!session) return;
    setCreateBusy(true);
    try {
      const participantIds = [session.user.id, ...input.users.map((user) => user.id)];
      const deviceGroups = await Promise.all(participantIds.map(getUserDevices));
      const devices = deviceGroups.flat();
      if (devices.length === 0) throw new Error("У участников нет активных устройств");
      const roomKey = await createRoomKey();
      const envelopes = await Promise.all(devices.map(async (device) => ({
        deviceId: device.id,
        envelope: await sealRoomKey(roomKey, device.encryptionPublicKey),
      })));
      const created = await apiRequest<{ chatId: string; existing: boolean }>("/api/chats", {
        method: "POST",
        body: {
          kind: input.kind,
          visibility: input.visibility,
          name: input.name,
          description: input.description,
          memberIds: input.users.map((user) => user.id),
          envelopes,
        },
      });
      if (!created.existing) keysRef.current.set(created.chatId, new Map([[1, roomKey]]));
      await refreshChats();
      setSelectedId(created.chatId);
      setCreateOpen(false);
    } finally {
      setCreateBusy(false);
    }
  };

  const saveGroupEpoch = (chatId: string, epoch: number, roomKey: Uint8Array) => {
    const epochs = keysRef.current.get(chatId) ?? new Map<number, Uint8Array>();
    epochs.set(epoch, roomKey);
    keysRef.current.set(chatId, epochs);
  };

  const requestGroupJoin = async (group: PublicGroup): Promise<void> => {
    await requestPublicGroupJoin(group.id);
  };

  const approveGroupJoin = async (chat: Chat, request: ChatJoinRequest): Promise<void> => {
    const bundle = await createGroupEpochBundle([...chat.members.map((member) => member.id), request.user.id]);
    const epoch = await approveChatJoinRequest(chat.id, request.id, bundle.envelopes);
    saveGroupEpoch(chat.id, epoch, bundle.roomKey);
    await refreshChats();
  };

  const rejectGroupJoin = async (chat: Chat, request: ChatJoinRequest): Promise<void> => {
    await rejectChatJoinRequest(chat.id, request.id);
    await refreshChats();
  };

  const inviteGroupUser = async (chat: Chat, user: User): Promise<void> => {
    const bundle = await createGroupEpochBundle([...chat.members.map((member) => member.id), user.id]);
    const epoch = await addChatMember(chat.id, user.id, bundle.envelopes);
    saveGroupEpoch(chat.id, epoch, bundle.roomKey);
    await refreshChats();
  };

  const removeGroupMember = async (chat: Chat, member: ChatMember, ban = false): Promise<void> => {
    const bundle = await createGroupEpochBundle(chat.members.filter((item) => item.id !== member.id).map((item) => item.id));
    const epoch = await removeChatMember(chat.id, member.id, bundle.envelopes, ban);
    saveGroupEpoch(chat.id, epoch, bundle.roomKey);
    await refreshChats();
  };

  const changeGroupRole = async (chat: Chat, member: ChatMember, role: "owner" | "admin" | "member"): Promise<void> => {
    await setChatMemberRole(chat.id, member.id, role);
    await refreshChats();
  };

  const unblockGroupUser = async (chat: Chat, ban: GroupBan): Promise<void> => {
    await unblockGroupMember(chat.id, ban.user.id);
  };

  const saveGroupProfile = async (chat: Chat, input: { avatarColor: string; description: string; name: string; visibility: "private" | "public" }): Promise<void> => {
    await updateGroupProfile(chat.id, input);
    await refreshChats();
  };

  const saveGroupAvatar = async (chat: Chat, file: File | null): Promise<void> => {
    await updateGroupAvatar(chat.id, file);
    await refreshChats();
  };

  const saveGroupNotifications = async (chat: Chat, level: "all" | "mentions" | "muted"): Promise<void> => {
    await updateChatNotifications(chat.id, level, level === "muted" ? new Date("9999-12-31T23:59:59.999Z").toISOString() : null);
    await refreshChats();
  };

  const makeGroupInvite = async (chat: Chat): Promise<string> => {
    const invite = await createGroupInvite(chat.id);
    const url = new URL(window.location.origin);
    url.searchParams.set("invite", invite.token);
    return url.toString();
  };

  const leaveCurrentGroup = async (chat: Chat): Promise<void> => {
    if (!session || !window.confirm(`Выйти из группы «${chat.name}»?`)) return;
    const remaining = chat.members.filter((member) => member.id !== session.user.id).map((member) => member.id);
    const bundle = await createGroupEpochBundle(remaining);
    await leaveGroup(chat.id, bundle.envelopes);
    keysRef.current.delete(chat.id);
    setGroupManagerOpen(false);
    setSelectedId(null);
    await refreshChats();
  };

  const deleteCurrentGroup = async (chat: Chat): Promise<void> => {
    const expected = chat.name ?? "Группа";
    const confirmation = window.prompt(`Удаление необратимо для всех участников. Введите название группы «${expected}»:`);
    if (confirmation !== expected) {
      if (confirmation !== null) throw new Error("Название группы введено неверно");
      return;
    }
    await deleteGroup(chat.id);
    keysRef.current.delete(chat.id);
    setGroupManagerOpen(false);
    selectedRef.current = null;
    setSelectedId(null);
    setMessages([]);
    await refreshChats();
  };

  const requestKey = async () => {
    if (!selectedChat || requestingKey) return;
    const chatId = selectedChat.id;
    const visiblyFailedEpochs = new Set(messages
      .filter((message) => !message.wire.retractedAt && message.authenticity === "verified" && !message.plain)
      .map((message) => message.wire.epoch));
    const requestedEpochs = [...visiblyFailedEpochs];
    if (!keysRef.current.get(chatId)?.has(selectedChat.currentEpoch)) requestedEpochs.push(selectedChat.currentEpoch);
    const uniqueEpochs = [...new Set(requestedEpochs)].sort((left, right) => left - right);
    setRequestingKey(true);
    setError(null);
    setNotice("Отправляем защищённый запрос устройствам участников…");
    try {
      const response = await retryKeyTransfer(() => apiRequest<{
        epochs: number[]; notifiedUsers: number; requestIds: string[];
      }>(`/api/chats/${chatId}/key-requests`, {
        method: "POST",
        body: uniqueEpochs.length > 0 ? { epochs: uniqueEpochs } : {},
      }));
      const targetEpochs = response.epochs;
      for (const epoch of targetEpochs) keysRef.current.get(chatId)?.delete(epoch);
      setNotice(response.notifiedUsers > 0
        ? `Запрошено ключей: ${targetEpochs.length}. Ожидаем автоматическую передачу…`
        : "Запрос сохранён. Сейчас сервер не видит ни одного активного устройства с нужным ключом.");

      for (let attempt = 0; attempt < 28; attempt += 1) {
        const delay = keyRequestPollDelay(attempt);
        if (delay > 0) await new Promise((resolve) => window.setTimeout(resolve, delay));
        const status = await retryKeyTransfer(() => apiRequest<{
          requests: Array<{ available: boolean; epoch: number; id: string; state: "pending" | "fulfilled" | "cancelled" }>;
        }>(`/api/chats/${chatId}/key-requests/status`));
        const relevant = status.requests.filter((item) => targetEpochs.includes(item.epoch));
        const serverHasDelivery = relevant.some((item) => item.available);
        const keysAlreadyOpened = targetEpochs.every((epoch) => keysRef.current.get(chatId)?.has(epoch));
        if (serverHasDelivery && !keysAlreadyOpened) await refreshChats();
        const decrypted = selectedRef.current === chatId && (serverHasDelivery || keysAlreadyOpened)
          ? await reloadChatMessages(chatId)
          : [];
        const unresolvedVisible = decrypted.some((message) => (
          targetEpochs.includes(message.wire.epoch)
          && !message.wire.retractedAt
          && message.authenticity === "verified"
          && !message.plain
        ));
        const missingKeys = targetEpochs.some((epoch) => !keysRef.current.get(chatId)?.has(epoch));
        if (!missingKeys && !unresolvedVisible) {
          setNotice(`Ключи комнаты получены. Восстановлено эпох: ${targetEpochs.length}.`);
          return;
        }
        if (attempt > 0 && attempt % 4 === 0) {
          await apiRequest(`/api/chats/${chatId}/key-requests/remind`, {
            method: "POST",
            body: { requestIds: response.requestIds },
          }).catch(() => undefined);
        }
      }
      setNotice("Запрос остаётся активным: мессенджер продолжит принимать ключи автоматически после появления другого устройства в сети.");
    } catch (caught) {
      setNotice(null);
      setError(caught instanceof Error ? caught.message : "Не удалось запросить ключи комнаты");
    } finally {
      setRequestingKey(false);
    }
  };

  const recoverKey = async () => {
    if (!selectedChat || !session) return;
    if (!window.confirm("Начать новый ключевой период? Старые сообщения без прежнего ключа останутся недоступны. Это действие нельзя отменить.")) return;
    setError(null);
    try {
      const deviceGroups = await Promise.all(selectedChat.members.map((member) => getUserDevices(member.id)));
      const devices = deviceGroups.flat();
      if (devices.length === 0) throw new Error("У участников нет активных устройств");
      const roomKey = await createRoomKey();
      const envelopes = await Promise.all(devices.map(async (device) => ({
        deviceId: device.id,
        envelope: await sealRoomKey(roomKey, device.encryptionPublicKey),
      })));
      const response = await apiRequest<{ epoch: number }>(`/api/chats/${selectedChat.id}/key-recovery`, {
        method: "POST",
        body: { envelopes },
      });
      keysRef.current.set(selectedChat.id, new Map([
        ...(keysRef.current.get(selectedChat.id) ?? new Map()),
        [response.epoch, roomKey],
      ]));
      await refreshChats();
      setError("Создан новый ключ комнаты. Старая история останется зашифрованной, но новые сообщения снова доступны.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось восстановить чат");
    }
  };

  const createTransferLink = async (): Promise<{ expiresAt: string; url: string }> => {
    if (!session) throw new Error("Сессия недоступна");
    setDeviceBusy(true);
    try {
      const linkId = crypto.randomUUID();
      const encrypted = await createEncryptedDeviceLink(keysRef.current, session.user.username, linkId);
      const created = await createDeviceLinkRequest({
        id: linkId,
        claimPublicKey: encrypted.claimPublicKey,
        ciphertext: encrypted.ciphertext,
        nonce: encrypted.nonce,
      });
      return {
        expiresAt: created.expiresAt,
        url: buildDeviceLinkUrl(`${window.location.origin}/`, linkId, encrypted.secret),
      };
    } finally {
      setDeviceBusy(false);
    }
  };

  const exportRecovery = async (passphrase: string): Promise<void> => {
    if (!session) throw new Error("Сессия недоступна");
    setDeviceBusy(true);
    try {
      const documentText = await createRecoveryDocument(keysRef.current, session.user.username, passphrase);
      const url = URL.createObjectURL(new Blob([documentText], { type: "application/json" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `jtuc-live-recovery-${session.user.username}-${new Date().toISOString().slice(0, 10)}.jtuc`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } finally {
      setDeviceBusy(false);
    }
  };

  const importRecovery = async (file: File, passphrase: string): Promise<number> => {
    if (!session || !identity) throw new Error("Сессия недоступна");
    if (file.size > 8_000_000) throw new Error("Файл резервной копии слишком большой");
    setDeviceBusy(true);
    try {
      const decrypted = await openRecoveryDocument(await file.text(), session.user.username, passphrase);
      const incoming = availableRoomKeys(decrypted, await getChats());
      if (roomKeyCount(incoming) === 0) throw new Error("В копии нет доступных ключей для текущих чатов");
      const envelopes = await sealKeyStoreForDevice(incoming, identity.encryptionPublicKey);
      await importDeviceKeyEnvelopes(envelopes);
      const imported = mergeRoomKeys(keysRef.current, incoming);
      await refreshChats();
      return imported;
    } finally {
      setDeviceBusy(false);
    }
  };

  const revokeDevice = async (device: AccountDevice): Promise<boolean> => {
    if (!identity || !session) throw new Error("Сессия недоступна");
    if (device.current || device.revokedAt) throw new Error("Это устройство нельзя отозвать");
    if (!window.confirm(`Отозвать устройство «${device.name}»? Для всех чатов будут созданы новые ключи.`)) return false;
    setDeviceBusy(true);
    try {
      const currentChats = await getChats();
      const deviceCache = new Map<string, Awaited<ReturnType<typeof getUserDevices>>>();
      const generated = new Map<string, { epoch: number; key: Uint8Array }>();
      const rekeys: Array<{ chatId: string; envelopes: Array<{ deviceId: string; envelope: string }> }> = [];
      for (const chat of currentChats) {
        const memberDevices = [];
        for (const member of chat.members) {
          let active = deviceCache.get(member.id);
          if (!active) {
            active = await getUserDevices(member.id);
            deviceCache.set(member.id, active);
          }
          memberDevices.push(...active);
        }
        const remaining = memberDevices.filter((item) => item.id !== device.id);
        if (remaining.length === 0) throw new Error(`Невозможно обновить ключи чата «${chat.name ?? "Личный чат"}»: нет активных устройств`);
        const key = await createRoomKey();
        const envelopes = [];
        for (const target of remaining) {
          envelopes.push({ deviceId: target.id, envelope: await sealRoomKey(key, target.encryptionPublicKey) });
        }
        generated.set(chat.id, { epoch: chat.currentEpoch + 1, key });
        rekeys.push({ chatId: chat.id, envelopes });
      }
      await revokeAccountDevice(device.id, rekeys);
      // Немедленно скрываем устройство и инвалидируем более старые параллельные ответы.
      deviceRefreshSequenceRef.current += 1;
      setDevices((current) => current.filter((item) => item.id !== device.id));
      for (const [chatId, next] of generated) {
        const epochs = keysRef.current.get(chatId) ?? new Map<number, Uint8Array>();
        epochs.set(next.epoch, next.key);
        keysRef.current.set(chatId, epochs);
      }
      await Promise.all([refreshChats(), refreshDevices()]);
      return true;
    } finally {
      setDeviceBusy(false);
    }
  };

  const decideDeviceApproval = async (approval: DeviceLinkApproval, decision: "approve" | "reject"): Promise<void> => {
    setDeviceBusy(true);
    try {
      await decideDeviceLinkApproval(approval.id, decision);
      await refreshDevices();
    } finally {
      setDeviceBusy(false);
    }
  };

  const updateLocalProtection = async (protection: "automatic" | "passphrase" | "system", passphrase?: string): Promise<void> => {
    if (!identity || !session) throw new Error("Сессия устройства недоступна");
    setDeviceBusy(true);
    try {
      if (protection === "automatic") {
        await saveIdentityAutomatic(session.user.username, identity);
      } else if (protection === "passphrase") {
        if (!passphrase || passphrase.length < 12) throw new Error("Локальная защитная фраза должна содержать не менее 12 символов");
        await saveIdentity(session.user.username, passphrase, identity);
        setAppLockPreference(session.user.username, "passphrase");
      } else if (isNativeMobileShell()) {
        await saveIdentityWithNativeSystem(session.user.username, identity);
      } else {
        const setup = await createSystemVaultSetup();
        await saveIdentityWithSystem(session.user.username, setup, identity);
        setAppLockPreference(session.user.username, "system");
      }
      setLocalProtection(protection);
    } finally {
      setDeviceBusy(false);
    }
  };

  const doLogout = async () => {
    // Remove the current device subscription while the authenticated session
    // still exists. This prevents stale notifications after logout.
    await disableWebPush().catch(() => undefined);
    await logout().catch(() => undefined);
    socket?.disconnect();
    keysRef.current.clear();
    setIdentity(null);
    setLocalProtection("automatic");
    setSession(null);
    setChats([]);
    setMessages([]);
  };

  const toggleDnd = async (): Promise<void> => {
    const next = !dndEnabled;
    await apiRequest<void>("/api/notifications/dnd", {
      method: "POST",
      body: { until: next ? new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString() : null },
    });
    setDndEnabled(next);
    setError(next ? "Режим «Не беспокоить» включён на 8 часов." : "Режим «Не беспокоить» выключен.");
  };

  const enablePush = async (): Promise<void> => {
    try {
      const enabled = await enableWebPush();
      setPushEnabled(enabled);
      setError(enabled ? "Push-уведомления о сообщениях и звонках включены." : "Браузер не разрешил Push-уведомления или сервер ещё не настроен.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось включить Push-уведомления");
    }
  };

  const calls = useCallController(socket);
  const [nativeAudioRoute, setNativeAudioRouteState] = useState<NativeCallAudioRoute>("earpiece");
  const getGroupCallKey = useCallback((chatId: string, epoch?: number): Uint8Array | null => {
    const chat = chats.find((item) => item.id === chatId);
    return chat ? keysRef.current.get(chatId)?.get(epoch ?? chat.currentEpoch) ?? null : null;
  }, [chats]);
  const groupCalls = useGroupCallController(socket, getGroupCallKey, session?.user.id);
  useEffect(() => {
    const updateRoute = (event: Event) => {
      const route = (event as CustomEvent<{ route?: NativeCallAudioRoute }>).detail?.route;
      if (route === "earpiece" || route === "speaker") setNativeAudioRouteState(route);
    };
    window.addEventListener("jtuc:native-call-audio-route", updateRoute as EventListener);
    return () => window.removeEventListener("jtuc:native-call-audio-route", updateRoute as EventListener);
  }, []);
  useEffect(() => {
    // React Native waits for this handshake before replaying an Answer/Decline
    // action received while the WebView was loading or the app was backgrounded.
    // The call-controller effects above are mounted before this effect runs.
    if (isNativeMobileShell()) postNativeMessage({ type: "bridge:ready" });
  }, []);
  useEffect(() => {
    if (!calls.call || !groupCalls.call) return;
    if (groupCalls.call.status === "incoming") groupCalls.declineCall();
    else if (calls.call.direction === "incoming") calls.endCall("declined");
  }, [calls.call, calls.endCall, groupCalls.call, groupCalls.declineCall]);
  useEffect(() => {
    if (!calls.call) { telemetryCallRef.current = null; return; }
    if (telemetryCallRef.current === calls.call.callId) return;
    if (calls.error) {
      telemetryCallRef.current = calls.call.callId;
      void apiRequest<void>("/api/telemetry", { method: "POST", body: { metric: "call_failed" } }).catch(() => undefined);
    } else if (calls.quality.route === "turn") {
      telemetryCallRef.current = calls.call.callId;
      void apiRequest<void>("/api/telemetry", { method: "POST", body: { metric: "turn_relay" } }).catch(() => undefined);
    }
  }, [calls.call, calls.error, calls.quality.route]);
  const peerName = useMemo(() => {
    if (!calls.call) return "Собеседник";
    for (const chat of chats) {
      const member = chat.members.find((item) => item.id === calls.call?.peerUserId);
      if (member) return member.displayName;
    }
    return "Собеседник";
  }, [calls.call, chats]);
  const peerProfile = useMemo(() => {
    if (!calls.call) return undefined;
    for (const chat of chats) {
      const member = chat.members.find((item) => item.id === calls.call?.peerUserId);
      if (member) return member;
    }
    return undefined;
  }, [calls.call, chats]);
  const toggleNativeAudioRoute = useCallback(() => {
    const next: NativeCallAudioRoute = nativeAudioRoute === "speaker" ? "earpiece" : "speaker";
    setNativeAudioRouteState(next);
    setNativeCallAudioRoute(next);
  }, [nativeAudioRoute]);

  if (session === undefined) return <main className="app-loading"><img alt="" className="loading-logo" src="/logo.png" /><span className="loader" /><b>Личный кабинет вебинаров</b><small>Подготавливаем вход</small></main>;
  if (!session) return <AuthScreen onAuthenticated={(nextSession, nextIdentity, protection) => { setSession(nextSession); setIdentity(nextIdentity); setLocalProtection(protection); }} />;
  if (!identity) return <UnlockScreen onLogout={doLogout} onUnlocked={(nextIdentity, protection) => { setIdentity(nextIdentity); setLocalProtection(protection); }} session={session} />;

  const callTarget = selectedChat?.kind === "direct" ? selectedChat.members.find((member) => member.id !== session.user.id) : undefined;
  const groupCallChat = groupCalls.call ? chats.find((chat) => chat.id === groupCalls.call?.chatId) ?? null : null;
  const groupCallName = groupCallChat?.name ?? "Групповой звонок";
  const groupParticipantProfiles = Object.fromEntries((groupCallChat?.members ?? []).map((member) => [member.id, member]));
  const groupCallAvatar = {
    avatarColor: groupCallChat?.avatarColor ?? "#617dff",
    avatarUrl: groupCallChat?.avatarUrl ?? null,
    displayName: groupCallName,
  };
  const selectChat = (chatId: string) => {
    const alreadySelected = selectedId === chatId;
    // Update the ref immediately; React does not run the selection effect after a same-row click.
    selectedRef.current = chatId;
    setSelectedId(chatId);
    setGroupManagerOpen(false);
    if (alreadySelected) void markChatRead(chatId).catch(() => undefined);
  };
  return (
    <div className="messenger-shell">
      <Sidebar chats={chats} connected={connected} dndEnabled={dndEnabled} pushEnabled={pushEnabled} onAdmin={() => setAdminOpen(true)} onBrowseGroups={() => setGroupDirectoryOpen(true)} onCreate={() => setCreateOpen(true)} onLogout={() => void doLogout()} onManageDevices={() => setDeviceManagerOpen(true)} onProfile={() => setProfileUserId(session.user.id)} onEnablePush={() => void enablePush()} onSelect={selectChat} onToggleDnd={() => void toggleDnd()} selectedId={selectedId} session={session} />
      <ChatView
        chat={selectedChat}
        error={error ?? calls.error ?? groupCalls.error}
        notice={notice}
        hasOlder={Boolean(nextBefore)}
        keyAvailable={Boolean(currentKey)}
        requestingKey={requestingKey}
        loading={loadingMessages}
        loadingOlder={loadingOlder}
        transfer={fileTransfer}
        messages={messages}
        groupCallActive={selectedChat ? groupCalls.activeCalls[selectedChat.id] ?? null : null}
        onBack={() => setSelectedId(null)}
        onCall={(video) => { if (!selectedChat || calls.call || groupCalls.call) return; if (selectedChat.kind === "group") void groupCalls.startCall(selectedChat.id, video); else if (callTarget) void calls.startCall(selectedChat.id, callTarget.id, video); }}
        onJoinGroupCall={() => { if (selectedChat) void groupCalls.joinActiveCall(selectedChat.id); }}
        onCancelTransfer={cancelFileTransfer}
        onDelete={deleteMessage}
        onDismissError={() => { setError(null); calls.clearError(); groupCalls.clearError(); }}
        onDismissNotice={() => setNotice(null)}
        onDownload={downloadFile}
        onEdit={editMessage}
        onForward={forwardMessage}
        onLoadOlder={loadOlderMessages}
        onManageGroup={() => setGroupManagerOpen(true)}
        onOpenProfile={setProfileUserId}
        onRequestKey={requestKey}
        onResolveMedia={resolveMedia}
        onRecoverKey={recoverKey}
        onPin={pinMessage}
        onReact={reactMessage}
        onReport={reportMessage}
        onSendFile={sendFile}
        onSendText={sendText}
        onSendVoice={sendVoice}
        onVerifySecurity={() => void openSecurityVerification()}
        securityStatus={selectedChat?.kind === "direct" ? securityStatuses[selectedChat.id] ?? "unknown" : "unknown"}
        session={session}
      />
      {createOpen && <CreateChatModal busy={createBusy} onClose={() => setCreateOpen(false)} onCreate={createChat} />}
      {deviceManagerOpen && <DeviceManagerModal approvals={deviceApprovals} busy={deviceBusy} devices={devices} localProtection={localProtection} onClose={() => setDeviceManagerOpen(false)} onCreateLink={createTransferLink} onDecideApproval={decideDeviceApproval} onExport={exportRecovery} onImport={importRecovery} onRefresh={refreshDevices} onRevoke={revokeDevice} onSetLocalProtection={updateLocalProtection} />}
      {groupDirectoryOpen && <GroupDirectoryModal onClose={() => setGroupDirectoryOpen(false)} onLoad={getPublicGroups} onRequestJoin={requestGroupJoin} />}
      {groupManagerOpen && selectedChat?.kind === "group" && <GroupManagerModal chat={selectedChat} onApprove={approveGroupJoin} onAvatar={saveGroupAvatar} onClose={() => setGroupManagerOpen(false)} onCreateInvite={makeGroupInvite} onDelete={deleteCurrentGroup} onInviteUser={inviteGroupUser} onLeave={leaveCurrentGroup} onLoadBans={getGroupBans} onLoadRequests={getChatJoinRequests} onNotifications={saveGroupNotifications} onProfile={saveGroupProfile} onReject={rejectGroupJoin} onRemove={removeGroupMember} onRole={changeGroupRole} onUnban={unblockGroupUser} session={session} />}
      {securityOpen && securityData && selectedChat?.kind === "direct" && <SecurityVerificationModal data={securityData} onClose={() => setSecurityOpen(false)} onRefresh={refreshSecurityVerification} onVerify={verifySecurity} peerName={selectedChat.members.find((member) => member.id !== session.user.id)?.displayName ?? "Собеседник"} />}
      {adminOpen && <AdminModal onClose={() => setAdminOpen(false)} />}
      {profileUserId && <ProfileModal onClose={() => setProfileUserId(null)} onUpdated={(user) => {
        setChats((current) => current.map((chat) => ({ ...chat, members: chat.members.map((member) => member.id === user.id ? { ...member, ...user } : member) })));
        if (user.id === session.user.id) setSession((current) => current ? { ...current, user: { ...current.user, ...user } } : current);
      }} userId={profileUserId} />}
      <CallOverlay call={calls.call} cameraEnabled={calls.cameraEnabled} localStream={calls.localStream} muted={calls.muted} nativeAudioRoute={isNativeMobileShell() ? nativeAudioRoute : undefined} onAccept={() => void calls.acceptCall()} onEnd={calls.endCall} onToggleAudioRoute={isNativeMobileShell() ? toggleNativeAudioRoute : undefined} onToggleCamera={calls.toggleCamera} onToggleMute={calls.toggleMute} ownProfile={session.user} peerName={peerName} peerProfile={peerProfile} quality={calls.quality} remoteStream={calls.remoteStream} />
      <GroupCallOverlay activeSpeakerId={groupCalls.activeSpeakerId} call={groupCalls.call} cameraEnabled={groupCalls.cameraEnabled} canEnd={Boolean(groupCalls.call?.canEnd)} canModerate={Boolean(groupCalls.call?.canModerate)} connectionState={groupCalls.connectionState} devices={groupCalls.devices} error={groupCalls.error} groupAvatar={groupCallAvatar} groupName={groupCallName} handRaised={groupCalls.handRaised} localStream={groupCalls.localStream} mediaAction={groupCalls.mediaAction} muted={groupCalls.muted} nativeAudioRoute={isNativeMobileShell() ? nativeAudioRoute : undefined} onAccept={() => void groupCalls.acceptCall()} onDecline={groupCalls.declineCall} onEnd={groupCalls.endCall} onLeave={groupCalls.leaveCall} onModerate={(action, userId) => void groupCalls.moderateParticipant(action, userId)} onSwitchDevice={(kind, deviceId) => void groupCalls.switchDevice(kind, deviceId)} onToggleAudioRoute={isNativeMobileShell() ? toggleNativeAudioRoute : undefined} onToggleCamera={() => void groupCalls.toggleCamera()} onToggleHand={() => void groupCalls.toggleHandRaised()} onToggleMute={() => void groupCalls.toggleMute()} onToggleScreen={() => void groupCalls.toggleScreen()} ownUserId={session.user.id} participantProfiles={groupParticipantProfiles} participants={groupCalls.participants} screenEnabled={groupCalls.screenEnabled} />
    </div>
  );
}

export default App;
